import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  type ProfileTemplate,
  ProfileTemplateFieldMode,
  ProfileTemplatesApi,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  cn,
  ghostAccentIconTileClass,
  Input,
} from '@up2stay/up2promo-ui';
import { Check, FileStack, Users } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { shellPaths } from '@/core/router/app-paths';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  ListEmptyState,
  ListPageBody,
  ListSearchField,
  ResourceCardGrid,
  ResourceCardGridItem,
  ResourceCardGridSkeleton,
  resourceInteractiveCardClass,
} from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { GridAddCard } from '@/core/components/layout/grid-add-card';
import { useInvalidateOnIonViewEnter } from '@/core/hooks/use-invalidate-on-ion-view-enter';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { MAX_TEMPLATE_NAME } from '@/core/components/profile-templates/template-name';
import { fetchAllProfileTemplatesPages } from '@/core/lib/profile-templates-fetch-all';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';
import { getApiErrorStatus } from '@/core/utils/api-error.utils';

const PROFILE_TEMPLATES_QUERY_KEYS = [['profile-templates']] as const;

export function ProfileTemplatesPage() {
  const dict = useDictionary();
  const queryClient = useQueryClient();
  const { navigateShell } = useNavigateShell();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { can } = usePermissions();
  const canManage = can(Permission.PROFILES_MANAGE_TEMPLATES);
  const loadErrorMessage = dict.workspace.profileTemplates.errors.loadFailed;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadErrorToastRef = useRef<string | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => setDebouncedSearch(search),
      300,
    );
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search]);

  const searchKey = debouncedSearch.trim();
  const queryKey = [
    'profile-templates',
    workspaceId,
    searchKey,
    canManage,
  ] as const;

  const {
    data: templates = [],
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchAllProfileTemplatesPages(workspaceId!, {
        ...(searchKey ? { search: searchKey } : {}),
        ...(!canManage ? { onlyMine: true } : {}),
      }),
    // The page stays mounted while `withRequiredPermissions` redirects.
    enabled: Boolean(workspaceId) && canManage,
  });

  useEffect(() => {
    if (!error) {
      loadErrorToastRef.current = null;
      return;
    }
    const status = getApiErrorStatus(error);
    const toastKey = `${status ?? 'unknown'}:${loadErrorMessage}`;
    if (loadErrorToastRef.current === toastKey) return;
    loadErrorToastRef.current = toastKey;
    console.error(error);
    toast.error(loadErrorMessage);
  }, [error, loadErrorMessage]);

  // Back from the editor (rename / delete) must refetch; the old callback
  // captured the first render's workspaceId and never ran (DEV-538).
  useInvalidateOnIonViewEnter(PROFILE_TEMPLATES_QUERY_KEYS);

  const handleCreate = async (name: string) => {
    if (!currentWorkspace) return;
    try {
      const res = await ProfileTemplatesApi.create({
        path: { workspaceId: currentWorkspace.id },
        body: {
          name,
          content: { modules: [] },
          fieldPolicies: {
            leadForm: ProfileTemplateFieldMode.LOCKED,
          },
        },
      });
      const created = res.data as ProfileTemplate | undefined;
      if (created?.id) {
        toast.success(dict.workspace.profileTemplates.createSuccess);
        queryClient.setQueryData<ProfileTemplate[]>(queryKey, (prev) => {
          const list = prev ?? [];
          if (list.some((t) => t.id === created.id)) return list;
          return [{ ...created, teamIds: created.teamIds ?? [] }, ...list];
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(dict.workspace.profileTemplates.errors.createFailed);
      throw err;
    }
  };

  const filtered = templates;
  const loading = isLoading || (isFetching && templates.length === 0);
  const isSearching = Boolean(searchKey);

  const breadcrumb = (
    <>
      {dict.nav.navDomainPeople}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {dict.nav.profileTemplates}
    </>
  );

  if (!currentWorkspace) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader
              title={dict.workspace.profileTemplates.title}
              breadcrumb={breadcrumb}
              description={dict.workspace.profileTemplates.selectWorkspace}
            />
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  const showAddCard = canManage && !isSearching;
  const showGrid = loading || showAddCard || filtered.length > 0;
  const slotCount = filtered.length + (showAddCard ? 1 : 0);

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <PageHeader
            title={dict.workspace.profileTemplates.title}
            breadcrumb={breadcrumb}
          />
          <ListPageBody>
            <ListSearchField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={dict.workspace.profileTemplates.searchPlaceholder}
            />

            {loading ? (
              <ResourceCardGridSkeleton count={3} />
            ) : showGrid ? (
              <ResourceCardGrid slotCount={Math.max(slotCount, 1)}>
                {showAddCard ? (
                  <ResourceCardGridItem>
                    <CreateTemplateCard
                      label={dict.workspace.profileTemplates.createCard}
                      cancelLabel={dict.workspace.teams.form.cancel}
                      saveLabel={dict.workspace.teams.inlineCreate.inlineSave}
                      namePlaceholder={
                        dict.workspace.profileTemplates.createNamePlaceholder
                      }
                      nameLabel={
                        dict.workspace.profileTemplates.createNameLabel
                      }
                      onCreate={handleCreate}
                    />
                  </ResourceCardGridItem>
                ) : null}
                {filtered.map((t) => (
                  <ResourceCardGridItem key={t.id}>
                    <TemplateListCard
                      template={t}
                      teamCountLabel={(t.teamIds?.length === 1
                        ? dict.workspace.profileTemplates.teamCountOne
                        : dict.workspace.profileTemplates.teamCount
                      ).replace('{count}', String(t.teamIds?.length ?? 0))}
                      onOpen={() =>
                        navigateShell(
                          shellPaths.moreProfileTemplateDetail(t.id),
                        )
                      }
                    />
                  </ResourceCardGridItem>
                ))}
              </ResourceCardGrid>
            ) : (
              <ListEmptyState
                icon={<FileStack className="h-6 w-6" />}
                title={
                  isSearching
                    ? dict.workspace.profileTemplates.noResults
                    : canManage
                      ? dict.workspace.profileTemplates.noTemplates
                      : dict.workspace.profileTemplates.noTemplatesForMember
                }
              />
            )}
          </ListPageBody>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function TemplateListCard({
  template,
  teamCountLabel,
  onOpen,
}: {
  template: ProfileTemplate;
  teamCountLabel: string;
  onOpen: () => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={template.name}
      className={cn(
        'flex h-full cursor-pointer flex-col',
        resourceInteractiveCardClass,
      )}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
    >
      <CardContent className="flex h-full flex-1 flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-[var(--r-control)]',
              ghostAccentIconTileClass,
            )}
            aria-hidden
          >
            <FileStack className="size-4" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h3
              className="line-clamp-2 text-[15.5px] font-bold leading-tight text-foreground [overflow-wrap:anywhere]"
              title={template.name}
            >
              {template.name}
            </h3>
            {template.description ? (
              <p className="line-clamp-2 text-[13.5px] leading-relaxed text-muted-foreground">
                {template.description}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-auto flex items-center gap-2 pt-1 text-[13.5px] leading-relaxed text-muted-foreground">
          <Users className="size-4 shrink-0" aria-hidden />
          <span>{teamCountLabel}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateTemplateCard({
  label,
  cancelLabel,
  saveLabel,
  namePlaceholder,
  nameLabel,
  onCreate,
}: {
  label: string;
  cancelLabel: string;
  saveLabel: string;
  namePlaceholder: string;
  /** Accessible name of the inline name field (DEV-540). */
  nameLabel: string;
  onCreate: (name: string) => void;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleStartCreating = () => {
    setIsCreating(true);
    setName('');
    requestAnimationFrame(() => nameInputRef.current?.focus());
  };

  const handleCancel = () => {
    setIsCreating(false);
    setName('');
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsSaving(true);
    try {
      await onCreate(trimmed);
      handleCancel();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isCreating) {
    return <GridAddCard label={label} onClick={handleStartCreating} />;
  }

  return (
    <Card className="flex h-full flex-col border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]/30 shadow-md">
      <CardContent className="flex flex-col gap-3 p-5 sm:p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          className="flex flex-col gap-3"
        >
          <div className="relative">
            <FileStack className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={namePlaceholder}
              aria-label={nameLabel}
              className="h-10 pl-9 text-[13.5px] font-semibold"
              maxLength={MAX_TEMPLATE_NAME}
              required
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={isSaving || !name.trim()}
              className="h-9 min-h-9"
            >
              <Check className="mr-1 h-3.5 w-3.5" />
              {saveLabel}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
              className="h-9 min-h-9 px-2 text-muted-foreground"
            >
              {cancelLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
